import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, LogOut, HelpCircle, Mic, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useLocationShare } from "../../contexts/LocationShareContext";
import { useTimeClock, fmtDuration, sessionSeconds } from "../TimeClock";
import { Switch } from "../ui/switch";
import { MOBILE_ELIGIBLE, DESKTOP_ONLY } from "../../lib/mobileRoutes";
import { chatApi } from "../../lib/api";
import { colorFor, initialsFor } from "../chat/ChatAvatar";

const ACTIVE_BG = "#EBF3FF";
const ACTIVE_COLOR = "#0063F7";
const INACTIVE_ICON = "#4B5563";
const INACTIVE_TEXT = "#1A1A2E";

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

/** Clock In/Out + Share Location dropdown, anchored under the profile name row. */
function ClockMenu({ open, onClose, clock }) {
  const location = useLocationShare();
  const [nowMs, setNowMs] = useState(Date.now());

  const status = clock.state?.status || "clocked_out";
  const clockedIn = status === "clocked_in" || status === "on_lunch";

  useEffect(() => {
    if (!open || !clockedIn) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, clockedIn]);

  if (!open) return null;

  const entry = clock.state?.entry;
  const liveSecs = entry ? sessionSeconds(entry, nowMs) : 0;
  const info = clockedIn
    ? `Clocked in at ${fmtTime(entry?.clockIn)} · ${fmtDuration(liveSecs)} so far`
    : clock.state && clock.state.todaySeconds > 0
    ? `Today: ${fmtDuration(clock.state.todaySeconds)} worked`
    : "Not clocked in yet today";

  const toggleClock = () => (clockedIn ? clock.clockOut() : clock.clockIn());
  const toggleLocation = async (val) => {
    if (val) {
      const ok = await location.enable();
      if (!ok) toast.error("Location permission is required to share your location.");
    } else {
      location.disable();
    }
  };

  return (
    <div
      className="relative mx-5 mb-3 rounded-2xl border border-slate-200 bg-slate-50"
      data-testid="mobile-drawer-clock-dropdown"
    >
      <button
        onClick={onClose}
        data-testid="mobile-drawer-clock-dropdown-close"
        aria-label="Close"
        className="absolute right-2 top-2 h-7 w-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-200"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="px-4 pt-3 pr-10 text-[12px] text-slate-500" data-testid="mobile-drawer-clock-info">
        {clock.loading ? "Loading…" : info}
      </div>
      <div className="px-3 pb-3 pt-2 flex flex-col gap-1">
        <button
          onClick={toggleClock}
          disabled={clock.busy || clock.loading}
          data-testid="mobile-drawer-clock-toggle-btn"
          className={`h-10 rounded-xl text-[14px] font-semibold text-white disabled:opacity-60 ${clockedIn ? "bg-rose-600 active:bg-rose-700" : "bg-emerald-600 active:bg-emerald-700"}`}
        >
          {clockedIn ? "Clock Out" : "Clock In"}
        </button>
        <div className="flex items-center justify-between px-1.5 py-2">
          <span className="text-[14px] font-medium" style={{ color: INACTIVE_TEXT }}>Share Location</span>
          <Switch
            checked={location.enabled}
            onCheckedChange={toggleLocation}
            disabled={location.busy}
            data-testid="mobile-drawer-share-location-toggle"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile app drawer — slides in from the left, dims the page behind it.
 * The ONE navigation surface for every role on mobile — items are simply
 * hidden/disabled per role and plan.
 *
 * Layout (top -> bottom):
 *   • Deep-blue header — centered logo (Tigris-hosted), white X close button
 *   • White panel: profile row (avatar + name + role)
 *   • Nav list — role-filtered mobile-eligible pages, active page highlighted
 *   • Divider -> Help & Support, Logout
 *   • Divider -> Desktop-only pages (present but disabled on mobile)
 *
 * Opens/closes via the global `open-mobile-drawer` custom event so any
 * component can trigger it without prop drilling.
 */
export default function MobileAppDrawer({ open, onClose }) {
  const { user, logout, hasModule } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [clockMenuOpen, setClockMenuOpen] = useState(false);
  const canTimeclock = !!user && hasModule("timeclock");
  const clock = useTimeClock(open && canTimeclock);
  const clockStatus = clock.state?.status || "clocked_out";
  const isClockedIn = clockStatus === "clocked_in" || clockStatus === "on_lunch";

  // Close the drawer whenever the route changes (tap a link, drawer dismisses).
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      prevPath.current = location.pathname;
      if (open) onClose && onClose();
    }
  }, [location.pathname, open, onClose]);

  // Lock body scroll while open so the drawer feels app-like.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!user) return null;

  // Calendar is the one MOBILE_ELIGIBLE item that lives on a dedicated
  // /mobile/calendar route (shared by all roles) rather than its own path —
  // matching the bottom nav's Calendar behavior.
  const resolvePath = (path) => (path === "/calendar" ? "/mobile/calendar" : path);

  const go = (path) => {
    onClose && onClose();
    navigate(resolvePath(path));
  };

  const currentPath = location.pathname;
  const isActive = (path) => {
    const resolved = resolvePath(path);
    return currentPath === resolved || currentPath.startsWith(resolved + "/");
  };

  const avatarSrc = user.avatarUrl ? chatApi.fileUrl(user.avatarUrl) : null;

  return (
    <div
      className={`mv md:hidden fixed inset-0 z-[70] ${open ? "" : "pointer-events-none"}`}
      data-testid="mobile-drawer-root"
      aria-hidden={!open}
    >
      {/* Dim backdrop — tap to close */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
        data-testid="mobile-drawer-backdrop"
      />

      {/* Sliding panel — leaves ~15% on the right (Gmail-style) */}
      <aside
        className={`absolute left-0 top-0 h-full w-[85%] max-w-[380px] bg-white shadow-2xl flex flex-col transition-transform duration-250 ease-out ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-drawer"
      >
        {/* Header — deep blue, centered logo, white close X */}
        <div
          className="relative shrink-0 flex flex-col items-center justify-center pb-6"
          style={{ backgroundColor: "#0A2F6E", paddingTop: "calc(env(safe-area-inset-top) + 1.5rem)" }}
          data-testid="mobile-drawer-header"
        >
          <button
            onClick={onClose}
            data-testid="mobile-drawer-close-btn"
            aria-label="Close menu"
            className="absolute right-4 active:scale-90 transition-transform"
            style={{ top: "calc(env(safe-area-inset-top) + 1.25rem)" }}
          >
            <X className="h-6 w-6 text-white" strokeWidth={2.2} />
          </button>
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/mobile-header-logo`}
            alt="Service Guru Pro"
            className="h-28 w-28 object-contain"
            data-testid="mobile-drawer-logo"
          />
        </div>

        <div className="flex-1 overflow-y-auto rounded-t-3xl -mt-4 bg-white relative">
          {/* Profile row */}
          <div className="flex items-center gap-3 px-5 pt-5 pb-4" data-testid="mobile-drawer-profile">
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center font-bold text-white text-[18px] overflow-hidden shrink-0"
              style={{ background: colorFor(user.name || user.email || "") }}
              data-testid="mobile-drawer-avatar"
            >
              {avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : initialsFor(user.name || user.email || "")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[19px] font-bold truncate" style={{ color: INACTIVE_TEXT }} data-testid="mobile-drawer-user-name">
                  {user.name || user.email}
                </div>
                {canTimeclock && (
                  <button
                    onClick={() => setClockMenuOpen((o) => !o)}
                    data-testid="mobile-drawer-clock-menu-btn"
                    aria-label="Clock & location menu"
                    className="h-8 w-8 -mr-1 shrink-0 rounded-full flex items-center justify-center active:bg-slate-100"
                  >
                    <MoreVertical className="h-5 w-5" color={INACTIVE_ICON} />
                  </button>
                )}
              </div>
              <div className="text-[13px] font-semibold uppercase tracking-wide text-slate-400" data-testid="mobile-drawer-user-role">
                {(user.role || "member").replace(/_/g, " ")}
              </div>
              {canTimeclock && (
                <div className="flex items-center gap-1.5 mt-1" data-testid="mobile-drawer-clock-status">
                  <span className={`h-1.5 w-1.5 rounded-full ${isClockedIn ? "bg-emerald-500" : "bg-red-500"}`} />
                  <span className={`text-[11.5px] font-semibold ${isClockedIn ? "text-emerald-600" : "text-red-600"}`}>
                    {isClockedIn ? "Clocked in" : "Clocked out"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {canTimeclock && (
            <ClockMenu open={clockMenuOpen} onClose={() => setClockMenuOpen(false)} clock={clock} />
          )}

          {/* Nav list */}
          <div className="px-2">
            <ul>
              {MOBILE_ELIGIBLE.filter((item) => !item.roles || item.roles.includes(user.role)).map((item) => {
                const Icon = item.icon;
                const testSlug = item.path.replace(/[^a-z]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
                const active = isActive(item.path);
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => go(item.path)}
                      data-testid={`mobile-drawer-nav-${testSlug}`}
                      className={`flex items-center gap-4 mx-2 my-0.5 px-3.5 py-3 rounded-xl text-left transition-colors ${active ? "" : "active:bg-slate-100"}`}
                      style={{ backgroundColor: active ? ACTIVE_BG : "transparent", width: "calc(100% - 1rem)" }}
                    >
                      <Icon className="h-6 w-6 shrink-0" color={active ? ACTIVE_COLOR : INACTIVE_ICON} strokeWidth={1.9} />
                      <span className="text-[17px] font-semibold" style={{ color: active ? ACTIVE_COLOR : INACTIVE_TEXT }}>
                        {item.title}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Divider + Help & Support / Logout */}
          <div className="mx-4 my-2 h-px bg-slate-200" />
          <div className="px-2 pb-2">
            <ul>
              <li>
                <button
                  onClick={() => { onClose && onClose(); window.dispatchEvent(new CustomEvent("open-help-chat")); }}
                  data-testid="mobile-drawer-help"
                  className="flex items-center gap-4 mx-2 my-0.5 px-3.5 py-3 rounded-xl text-left active:bg-slate-100"
                  style={{ width: "calc(100% - 1rem)" }}
                >
                  <HelpCircle className="h-6 w-6 shrink-0" color={INACTIVE_ICON} strokeWidth={1.9} />
                  <span className="text-[17px] font-semibold" style={{ color: INACTIVE_TEXT }}>Help &amp; Support</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => go("/voice-commands")}
                  data-testid="mobile-drawer-voice-commands"
                  className="flex items-center gap-4 mx-2 my-0.5 px-3.5 py-3 rounded-xl text-left active:bg-slate-100"
                  style={{ width: "calc(100% - 1rem)" }}
                >
                  <Mic className="h-6 w-6 shrink-0" color={INACTIVE_ICON} strokeWidth={1.9} />
                  <span className="text-[17px] font-semibold" style={{ color: INACTIVE_TEXT }}>Voice Commands</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => { onClose && onClose(); logout && logout(); }}
                  data-testid="mobile-drawer-signout"
                  className="flex items-center gap-4 mx-2 my-0.5 px-3.5 py-3 rounded-xl text-left active:bg-slate-100"
                  style={{ width: "calc(100% - 1rem)" }}
                >
                  <LogOut className="h-6 w-6 shrink-0" color={INACTIVE_ICON} strokeWidth={1.9} />
                  <span className="text-[17px] font-semibold" style={{ color: INACTIVE_TEXT }}>Logout</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Desktop-only pages — visible so technicians/admins know they exist,
              disabled here since they don't have a mobile-optimized surface yet. */}
          <div className="mx-4 my-2 h-px bg-slate-200" />
          <div className="px-2 pb-6">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 px-5 mb-1">
              Desktop Only
            </div>
            <ul>
              {DESKTOP_ONLY.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <div
                      data-testid={`mobile-drawer-desktop-only-${item.path.replace(/[^a-z]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                      className="flex items-center gap-4 mx-2 my-0.5 px-3.5 py-3 rounded-xl select-none cursor-not-allowed"
                      title="Open on desktop to use this feature"
                    >
                      <Icon className="h-6 w-6 shrink-0" color="#CBD5E1" strokeWidth={1.9} />
                      <span className="text-[17px] font-semibold text-slate-300 flex-1">{item.title}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">Desktop</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
}
